import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useStory } from '../../context/StoryContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const genres = ['Fantasy', 'Sci-Fi', 'Mystery', 'Cyberpunk', 'Romance', 'Horror'];
const povs = ['1st Person (I)', '2nd Person (You)', '3rd Person Limited', '3rd Person Omniscient'];

import { useAuth } from '../../context/AuthContext';

const WorldSetup = () => {
  const navigate = useNavigate();
  const { state, updateWorld, updateStoryTitle } = useStory();
  const { user } = useAuth();
  const { world } = state;

  React.useEffect(() => {
    if (state.supabaseStoryId && state.ownerId && user && state.ownerId !== user.id) {
      alert("Only owners can modify the story setup.");
      navigate('/story');
    }
  }, [state.supabaseStoryId, state.ownerId, user, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    navigate('/setup/character');
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        // Compress the image before setting to state to avoid massive base64 strings
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 1200;
        const MAX_HEIGHT = 1200;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Use a reasonable quality jpeg
        const compressedBase64 = canvas.toDataURL('image/jpeg', 0.6);
        updateWorld({ backgroundImage: compressedBase64 });
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = null; // reset input
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
        <div className="mb-8">
          <p className="text-amber-500 text-sm font-bold tracking-widest uppercase mb-2">Step 1 of 3</p>
          <h2 className="text-4xl font-serif font-bold text-gray-900 dark:text-white">Shape the Universe</h2>
          <p className="text-gray-900 dark:text-purple-400 mt-2">Establish the foundational rules of your story's reality.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-8 bg-gray-50/50 dark:bg-purple-900/20 p-8 rounded-3xl border border-gray-100 dark:border-purple-800 shadow-xl">
          <div>
            <label className="label-text">Story Title</label>
            <input
              type="text"
              value={state.storyTitle === 'Untitled Story' ? '' : state.storyTitle}
              onChange={(e) => updateStoryTitle(e.target.value)}
              className="input-field"
              placeholder="e.g., The Echoes of Eternity"
              required
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="relative">
              <label className="label-text">Story Genre</label>
              <div className="relative">
                <select 
                  value={world.genre} 
                  onChange={(e) => updateWorld({ genre: e.target.value })} 
                  className="input-field appearance-none cursor-pointer pr-10" 
                  required
                >
                  <option value="" disabled>Select a genre...</option>
                  {genres.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-900 dark:text-purple-400">
                  <SafeIcon icon={FiIcons.FiChevronDown} />
                </div>
              </div>
            </div>
            <div className="relative">
              <label className="label-text">Narrative POV</label>
              <div className="relative">
                <select 
                  value={world.pov} 
                  onChange={(e) => updateWorld({ pov: e.target.value })} 
                  className="input-field appearance-none cursor-pointer pr-10"
                >
                  {povs.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-900 dark:text-purple-400">
                  <SafeIcon icon={FiIcons.FiChevronDown} />
                </div>
              </div>
            </div>
          </div>
          <div>
            <label className="label-text">Setting Description</label>
            <textarea 
              value={world.setting} 
              onChange={(e) => updateWorld({ setting: e.target.value })} 
              className="input-field min-h-[140px] resize-none" 
              placeholder="e.g., A sprawling neon metropolis where the rain never stops and corporations rule the streets..." 
              required 
            />
          </div>
          <div>
            <label className="label-text">Background Image (Optional)</label>
            <div className="flex items-center space-x-2">
              {world.backgroundImage && world.backgroundImage.startsWith('data:image') ? (
                <div className="flex-1 bg-gray-100 dark:bg-purple-900/50 px-4 py-3 rounded-xl border border-gray-200 dark:border-purple-700 text-sm text-gray-900 dark:text-purple-300 truncate">
                  Local image uploaded
                  <button type="button" onClick={() => updateWorld({ backgroundImage: '' })} className="ml-2 text-red-500 hover:text-red-700 font-bold">Clear</button>
                </div>
              ) : (
                <input
                  type="text"
                  value={world.backgroundImage || ''}
                  onChange={(e) => updateWorld({ backgroundImage: e.target.value })}
                  className="input-field flex-1"
                  placeholder="Image URL or Upload..."
                />
              )}
              <label className="cursor-pointer bg-gray-100 hover:bg-purple-200 dark:bg-purple-900/50 dark:hover:bg-purple-800 text-gray-900 dark:text-purple-300 px-4 py-3 rounded-xl border border-gray-200 dark:border-purple-700 font-medium transition-colors flex items-center space-x-2">
                <SafeIcon icon={FiIcons.FiUploadCloud} />
                <span className="hidden sm:inline">Upload</span>
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              </label>
            </div>
            <p className="text-xs text-gray-600 mt-1">This image will faintly appear in the background of your story.</p>
          </div>
          <div className="pt-4 pb-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={world.isPublic || false}
                onChange={(e) => updateWorld({ isPublic: e.target.checked })}
                className="w-5 h-5 rounded border-purple-300 text-amber-500 focus:ring-amber-500"
              />
              <span className="label-text mb-0">Make Story Public</span>
            </label>
            <p className="text-sm text-gray-600 mt-1 pl-8">Public stories appear in the global directory and allow others to request to join.</p>
          </div>
          <div className="flex justify-end pt-6 border-t border-gray-100 dark:border-purple-800">
            <button type="submit" className="btn-primary flex items-center space-x-3 px-10 text-gray-900 dark:text-white">
              <span className="text-lg">Next: Create Character</span>
              <SafeIcon icon={FiIcons.FiArrowRight} />
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default WorldSetup;